import React, { useState, useEffect } from "react";
import { orderService } from "../services/apiService";

const ReturnHistoryModal = ({ orderId, billNo, onClose }) => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await orderService.getReturnsByOrderId(orderId);
        setReturns(Array.isArray(data) ? data : []);
      } catch {
        setReturns([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [orderId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-blue-500 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-history text-white text-lg" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm sm:text-base">Return History</h3>
              <p className="text-white/80 text-xs">Order #{orderId} • {billNo || "-"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition">
            <i className="fas fa-times text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <i className="fas fa-spinner fa-spin text-blue-400 text-xl" />
            </div>
          ) : returns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <i className="fas fa-box-open text-4xl mb-3" />
              <p className="text-sm">No returns found for this order.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {returns.map((ret) => (
                <div key={ret.returnId} className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Return header */}
                  <div className="bg-gray-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200">
                    <div>
                      <span className="text-xs font-bold text-gray-700">Return #{ret.returnId}</span>
                      <span className="text-xs text-gray-400 ml-2">
                        {ret.returnDate ? new Date(ret.returnDate).toLocaleString() : "-"}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                      {ret.status}
                    </span>
                  </div>

                  {/* Return items */}
                  <div className="px-4 py-3 space-y-2">
                    {(ret.items || []).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{item.productName}</p>
                          <p className="text-xs text-gray-400">Qty returned: {item.returnQuantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-red-600">-₹{Number(item.refundAmount).toFixed(2)}</p>
                          <p className="text-xs text-gray-400">Profit: -₹{Number(item.profitReversed).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Return summary */}
                  <div className="bg-red-50 px-4 py-2.5 border-t border-red-100 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Reason:</span> {ret.reason || "-"}
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-red-700">Refund: ₹{Number(ret.refundAmount).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{ret.refundMode} • Profit reversed: ₹{Number(ret.profitReversed).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3 bg-gray-50 flex justify-end flex-shrink-0">
          <button onClick={onClose}
            className="px-5 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-medium transition">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnHistoryModal;
